import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtDateTimeShort, fmtISODate } from "@/lib/dates";
import {
  MapPin,
  CheckCircle2,
  Clock,
  Loader2,
  Camera,
  Upload,
  Trash2,
  Image as ImageIcon,
  AlertTriangle,
  Link2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const VISIT_RECORDERS = ["registered_manager", "deputy_manager", "team_leader", "care_worker", "bank_worker", "super_admin"];
const MGMT = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "super_admin"];

function formatTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function _suFromVisit(v) {
  // Backend embeds service_user_name/address on /visits responses so workers
  // can render even without permission to list /service-users
  if (!v?.service_user_name) return null;
  return {
    id: v.service_user_id,
    full_name: v.service_user_name,
    preferred_name: v.service_user_preferred_name,
    address: v.service_user_address,
  };
}

export default function Visits() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const { user } = useAuth();
  const load = () =>
    Promise.all([api.get("/visits"), api.get("/service-users")]).then(([v, su]) => {
      setItems(v.data.items || []);
      const map = {};
      (su.data.items || []).forEach((s) => {
        map[s.id] = s;
      });
      setUsers(map);
    });
  useEffect(() => {
    load();
  }, []);

  const today = fmtISODate(new Date());
  const todayList = items.filter((v) => (v.scheduled_start || "").startsWith(today));
  const others = items.filter((v) => !(v.scheduled_start || "").startsWith(today));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {user.role === "care_worker" || user.role === "bank_worker" ? "My visits" : "Visits"}
        </h1>
        <p className="text-sm text-slate-600 inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-[#0D7377]" />
          Clock in and out directly here — Care Connect pushes the punch to Jibble for you.
        </p>
      </div>

      <section>
        <h2 className="font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">Today</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {todayList.map((v) => (
            <VisitCard key={v.id} v={v} su={users[v.service_user_id] || _suFromVisit(v)} reload={load} userRole={user.role} />
          ))}
          {todayList.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="visits-today-empty">No visits scheduled today.</p>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">Other visits</h2>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm divide-y divide-slate-100">
          {others.slice(0, 30).map((v) => {
            const su = users[v.service_user_id] || _suFromVisit(v);
            return (
              <div key={v.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`visit-row-${v.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-900">{su?.full_name || "—"}</p>
                  <p className="text-xs text-slate-500">
                    {fmtDateTimeShort(v.scheduled_start)} · {v.visit_type}
                    {su?.address?.postcode && <span className="ml-1">· {su.address.line1}, {su.address.postcode}</span>}
                    {v.actual_duration_mins != null && (
                      <span className="ml-2 text-[#0D7377]">· Jibble {v.actual_duration_mins} min</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {v.linked_jibble_entry_id && <Link2 className="h-3.5 w-3.5 text-emerald-600" data-testid={`jibble-linked-${v.id}`} />}
                  {v.short_visit_flag && <StatusBadge status="overdue" label="Short" />}
                  <StatusBadge status={v.status} />
                </div>
              </div>
            );
          })}
          {others.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No other visits.</p>}
        </div>
      </section>
    </div>
  );
}

function VisitCard({ v, su, reload, userRole }) {
  const canRecord = VISIT_RECORDERS.includes(userRole);
  const canRelink = MGMT.includes(userRole) || userRole === "team_leader";
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const [tasks, setTasks] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showRelink, setShowRelink] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [notes, setNotes] = useState({
    activities_completed: [],
    personal_care: [],
    meals: "",
    fluids: "",
    mood: "",
    physical_observations: "",
    medication_notes: "",
    concerns: "",
  });
  const [photos, setPhotos] = useState(v.photos || []);
  const [moodPrompt, setMoodPrompt] = useState(false);

  useEffect(() => {
    if (canRecord && (v.status === "in_progress" || v.status === "scheduled" || v.status === "completed")) {
      api
        .get(`/visits/${v.id}/tasks`)
        .then(({ data }) => {
          setTasks((data.tasks || []).map((t) => ({ ...t, completed: false, reason_if_not_completed: "" })));
        })
        .catch(() => {});
      if (v.status === "scheduled") {
        api.get(`/visits/${v.id}/briefing`).then(({ data }) => setBriefing(data)).catch(() => {});
      }
      if (v.visit_notes) {
        setNotes((n) => ({ ...n, ...v.visit_notes }));
      }
    }
  }, [v.id, v.status, canRecord, v.visit_notes]);

  const acknowledgeBriefing = async () => {
    try {
      await api.post(`/visits/${v.id}/briefing/acknowledge`);
      setBriefing({ ...(briefing || {}), already_acknowledged: true });
      setBriefingOpen(false);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };

  const submitNotes = async () => {
    const incomplete = tasks.find((t) => !t.completed && !(t.reason_if_not_completed || "").trim());
    if (incomplete) {
      setMsg(`Task "${incomplete.label}" is incomplete. Tick it off or add a reason.`);
      return;
    }
    setBusy("save");
    setMsg("");
    try {
      const { data } = await api.post(`/visits/${v.id}/notes`, {
        ...notes,
        tasks,
        photo_keys: photos.map((p) => p.key),
      });
      setMsg(`Notes saved. Visit status: ${data.status}${data.short_visit ? " (short-visit flagged)" : ""}`);
      reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const openRelink = async () => {
    setShowRelink(true);
    setCandidates(null);
    try {
      const { data } = await api.get(`/visits/${v.id}/jibble-candidates`);
      setCandidates(data.items || []);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Could not load Jibble candidates");
    }
  };

  const relinkTo = async (jibbleEntryId) => {
    try {
      await api.post(`/visits/${v.id}/link-jibble-entry`, { jibble_entry_id: jibbleEntryId });
      setShowRelink(false);
      reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };

  const unlinkJibble = async () => {
    if (!window.confirm("Unlink this visit from its Jibble entry? Status will revert to 'scheduled'.")) return;
    try {
      await api.post(`/visits/${v.id}/unlink-jibble`);
      setShowRelink(false);
      reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };

  const onPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy("photo");
    setMsg("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/visits/${v.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.photo]);
      }
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.delete(`/visits/${v.id}/photos/${photoId}`);
      setPhotos((p) => p.filter((x) => x.id !== photoId));
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message);
    }
  };

  const { user: currentUser } = useAuth();
  const isAssignedWorker = currentUser?.id === v.worker_id;

  const jibbleClockIn = async () => {
    setBusy("jibble_in");
    setMsg("");
    try {
      const { data } = await api.post(`/visits/${v.id}/jibble-clock-in`);
      setMsg(data.already ? "Already clocked in via Jibble — visit marked in progress." : "Clocked in via Jibble.");
      reload();
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  const jibbleClockOut = async () => {
    if (!window.confirm("Clock out of this visit via Jibble? You can still edit notes after.")) return;
    setBusy("jibble_out");
    setMsg("");
    try {
      const { data } = await api.post(`/visits/${v.id}/jibble-clock-out`);
      const short = data.short_visit_flag ? " (short-visit flagged)" : "";
      setMsg(data.already ? `Already clocked out — duration ${data.actual_duration_mins ?? "?"} min${short}` : `Clocked out · ${data.actual_duration_mins ?? "?"} min${short}`);
      reload();
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  const setTaskField = (idx, field, val) => setTasks((arr) => arr.map((t, i) => (i === idx ? { ...t, [field]: val } : t)));

  const sched = (v.scheduled_start || "").slice(11, 16);
  const showNotesForm = canRecord && (v.status === "in_progress" || (v.status === "scheduled" && showNotes) || (v.status === "completed" && !v.notes_submitted_at));

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 cc-glow" data-testid={`visit-card-${v.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">{sched}</p>
          <p className="font-heading text-lg font-semibold text-slate-900">{su?.full_name || "Service user"}</p>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" /> {su?.address?.line1}, {su?.address?.postcode}
          </p>
        </div>
        <StatusBadge status={v.status} />
      </div>

      {/* Jibble status pill + clock-in / clock-out buttons */}
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`jibble-status-${v.id}`}>
        {v.actual_end ? (
          <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-2 py-1">
            <Link2 className="h-3 w-3" />
            Jibble: {formatTime(v.actual_start) || "?"} → {formatTime(v.actual_end)}
            {v.actual_duration_mins != null && <span className="font-semibold">· {v.actual_duration_mins} min</span>}
          </span>
        ) : v.actual_start ? (
          <span className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-2 py-1">
            <Clock className="h-3 w-3" /> Clocked in {formatTime(v.actual_start)} — in progress
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-md px-2 py-1">
            <Clock className="h-3 w-3" /> Not clocked in
          </span>
        )}
        {isAssignedWorker && !v.actual_start && (
          <button
            onClick={jibbleClockIn}
            disabled={busy === "jibble_in"}
            data-testid={`jibble-clock-in-${v.id}`}
            className="inline-flex items-center gap-1.5 text-xs bg-[#0D7377] hover:bg-[#0A5C5F] disabled:opacity-50 text-white rounded-md px-3 py-1 font-medium"
          >
            {busy === "jibble_in" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clock className="h-3 w-3" />}
            Clock in (Jibble)
          </button>
        )}
        {isAssignedWorker && v.actual_start && !v.actual_end && (
          <button
            onClick={jibbleClockOut}
            disabled={busy === "jibble_out"}
            data-testid={`jibble-clock-out-${v.id}`}
            className="inline-flex items-center gap-1.5 text-xs bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-md px-3 py-1 font-medium"
          >
            {busy === "jibble_out" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            Clock out (Jibble)
          </button>
        )}
        {v.short_visit_flag && (
          <span className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-2 py-1">
            <AlertTriangle className="h-3 w-3" /> Short visit
          </span>
        )}
        {canRelink && (
          <button
            onClick={openRelink}
            data-testid={`relink-jibble-${v.id}`}
            className="ml-auto text-xs text-[#0D7377] hover:underline"
          >
            {v.linked_jibble_entry_id ? "Re-assign…" : "Link Jibble entry…"}
          </button>
        )}
      </div>

      {/* First-visit briefing prompt for scheduled visits */}
      {canRecord && v.status === "scheduled" && briefing?.required && !briefing.already_acknowledged && (
        <button
          onClick={() => setBriefingOpen(true)}
          data-testid={`briefing-prompt-${v.id}`}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 py-2.5 rounded-md font-medium text-sm"
        >
          <AlertTriangle className="h-4 w-4" /> Review First-Visit Briefing
        </button>
      )}

      {/* Notes form — visible when in_progress, or expanded manually */}
      {canRecord && !showNotesForm && (v.status === "scheduled" || (v.status === "completed" && v.notes_submitted_at)) && (
        <button
          onClick={() => setShowNotes(true)}
          data-testid={`show-notes-form-${v.id}`}
          className="mt-3 text-xs font-medium text-[#0D7377] hover:underline"
        >
          {v.notes_submitted_at ? "View/edit notes →" : "Start writing notes →"}
        </button>
      )}

      {showNotesForm && (
        <div className="mt-4 space-y-3">
          <fieldset className="border border-slate-200 rounded-md p-3" data-testid={`visit-tasks-${v.id}`}>
            <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Tasks (from care plan)</legend>
            {tasks.length === 0 && <p className="text-xs text-slate-500">No tasks defined for this visit type.</p>}
            <div className="space-y-2">
              {tasks.map((t, i) => (
                <div key={t.task_id || i} className="border-l-2 border-slate-200 pl-2">
                  <label className="text-sm inline-flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={!!t.completed}
                      onChange={(e) => setTaskField(i, "completed", e.target.checked)}
                      className="mt-1"
                      data-testid={`task-checkbox-${v.id}-${i}`}
                    />
                    <span className={t.completed ? "text-slate-700" : "text-slate-900 font-medium"}>{t.label}</span>
                  </label>
                  {!t.completed && (
                    <input
                      value={t.reason_if_not_completed || ""}
                      onChange={(e) => setTaskField(i, "reason_if_not_completed", e.target.value)}
                      placeholder="Reason if not completed (required)"
                      className="mt-1 w-full px-2 py-1 text-xs border border-amber-300 bg-amber-50 rounded"
                      data-testid={`task-reason-${v.id}-${i}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </fieldset>

          <fieldset className="border border-slate-200 rounded-md p-3" data-testid={`visit-mood-fieldset-${v.id}`}>
            <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Mood &amp; wellbeing</legend>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {[
                { v: "Bright", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                { v: "Calm", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                { v: "Settled", color: "bg-slate-100 text-slate-700 border-slate-300" },
                { v: "Quiet", color: "bg-slate-100 text-slate-700 border-slate-300" },
                { v: "Tearful", color: "bg-amber-50 text-amber-800 border-amber-300" },
                { v: "Anxious", color: "bg-amber-50 text-amber-800 border-amber-300" },
                { v: "Withdrawn", color: "bg-amber-50 text-amber-800 border-amber-300" },
                { v: "Distressed", color: "bg-red-50 text-red-800 border-red-300" },
                { v: "Confused", color: "bg-amber-50 text-amber-800 border-amber-300" },
                { v: "Agitated", color: "bg-red-50 text-red-800 border-red-300" },
              ].map((m) => {
                const selected = (notes.mood || "").startsWith(m.v);
                return (
                  <button
                    type="button"
                    key={m.v}
                    onClick={() => {
                      setNotes({ ...notes, mood: m.v + ((notes.mood || "").startsWith(m.v) ? "" : " — ") });
                      setMoodPrompt(["Distressed", "Agitated", "Tearful", "Anxious"].includes(m.v));
                    }}
                    className={`text-xs px-2 py-1 border rounded-full ${selected ? m.color + " ring-2 ring-offset-1 ring-[#0D7377]" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                    data-testid={`mood-pill-${m.v.toLowerCase()}`}
                  >
                    {m.v}
                  </button>
                );
              })}
            </div>
            <textarea
              placeholder="Free-text detail — what did you observe?"
              value={notes.mood}
              onChange={(e) => {
                setNotes({ ...notes, mood: e.target.value });
                setMoodPrompt(/distress|distressed|upset|anxious|agitated|tearful/i.test(e.target.value));
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              rows={2}
              data-testid={`visit-mood-${v.id}`}
            />
            {moodPrompt && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5" /> The person appears distressed. Add detail in Concerns and consider raising an incident or safeguarding concern.
              </p>
            )}
          </fieldset>

          <textarea
            placeholder="Meals & fluids"
            value={notes.meals}
            onChange={(e) => setNotes({ ...notes, meals: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            rows={2}
          />
          <textarea
            placeholder="Concerns / observations"
            value={notes.concerns}
            onChange={(e) => setNotes({ ...notes, concerns: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            rows={2}
            data-testid={`visit-concerns-${v.id}`}
          />

          <fieldset className="border border-slate-200 rounded-md p-3" data-testid={`visit-photos-${v.id}`}>
            <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Photos</legend>
            <div className="flex flex-wrap gap-2 items-center">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50" data-testid={`take-photo-button-${v.id}`}>
                <Camera className="h-4 w-4" /> Take photo
                <input type="file" accept="image/*" capture="environment" onChange={onPhotoSelect} className="hidden" />
              </label>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50" data-testid={`upload-photo-button-${v.id}`}>
                <Upload className="h-4 w-4" /> Upload from device
                <input type="file" accept="image/*" multiple onChange={onPhotoSelect} className="hidden" />
              </label>
              {busy === "photo" && (
                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {photos.map((p) => (
                <PhotoThumb key={p.id} visitId={v.id} photo={p} onRemove={() => removePhoto(p.id)} />
              ))}
            </div>
          </fieldset>

          <button
            onClick={submitNotes}
            disabled={busy === "save"}
            data-testid={`save-notes-button-${v.id}`}
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-md font-semibold min-h-[48px]"
          >
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save visit notes
          </button>
        </div>
      )}

      {!canRecord && (
        <Link to={`/service-users/${v.service_user_id}`} className="inline-block mt-3 text-sm text-[#0D7377] font-medium">
          View service user →
        </Link>
      )}

      {msg && <p className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">{msg}</p>}

      {briefingOpen && briefing && (
        <BriefingModal briefing={briefing} onClose={() => setBriefingOpen(false)} onAcknowledge={acknowledgeBriefing} />
      )}

      {showRelink && (
        <JibbleRelinkModal
          visit={v}
          candidates={candidates}
          onClose={() => setShowRelink(false)}
          onPick={relinkTo}
          onUnlink={v.linked_jibble_entry_id ? unlinkJibble : null}
        />
      )}
    </div>
  );
}

function JibbleRelinkModal({ visit, candidates, onClose, onPick, onUnlink }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-2" data-testid="jibble-relink-modal">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-2xl">
        <header className="border-b border-slate-200 px-5 py-3">
          <p className="text-xs uppercase tracking-wider text-slate-500">Re-assign Jibble entry</p>
          <h3 className="font-heading text-lg font-semibold text-slate-900">
            Visit {fmtDateTimeShort(visit.scheduled_start)}
          </h3>
        </header>
        <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
          {candidates === null ? (
            <p className="text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading Jibble entries near this visit…
            </p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-slate-500">No Jibble entries for this worker within ±12 hours of the scheduled start. Try syncing first.</p>
          ) : (
            candidates.map((c) => {
              const isCurrent = c.id === visit.linked_jibble_entry_id;
              return (
                <button
                  key={c.id}
                  onClick={() => onPick(c.id)}
                  disabled={isCurrent}
                  data-testid={`jibble-candidate-${c.id}`}
                  className={`w-full text-left border rounded-md px-3 py-2 ${isCurrent ? "bg-emerald-50 border-emerald-200" : "hover:bg-slate-50 border-slate-200"}`}
                >
                  <p className="text-sm font-medium text-slate-900">
                    {formatTime(c.start) || "—"} → {formatTime(c.end) || "in progress"}
                    {c.duration_seconds && (
                      <span className="text-slate-500 ml-2">· {Math.round(c.duration_seconds / 60)} min</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {fmtDate(c.start)} {isCurrent && <span className="ml-2 text-emerald-700 font-semibold">· current link</span>}
                  </p>
                </button>
              );
            })
          )}
        </div>
        <footer className="flex justify-between items-center border-t border-slate-200 px-5 py-3">
          {onUnlink ? (
            <button onClick={onUnlink} data-testid="jibble-unlink-button" className="text-sm text-rose-700 hover:underline">
              Unlink
            </button>
          ) : (
            <span />
          )}
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md text-sm">
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function PhotoThumb({ visitId, photo, onRemove }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    api
      .get(`/visits/${visitId}/photos/${photo.id}`)
      .then(({ data }) => setUrl(data.url))
      .catch(() => {});
  }, [visitId, photo.id]);
  return (
    <div className="relative w-20 h-20 border border-slate-200 rounded-md overflow-hidden bg-slate-100 group" data-testid={`photo-thumb-${photo.id}`}>
      {url ? <img src={url} alt="visit" className="w-full h-full object-cover" /> : <ImageIcon className="h-6 w-6 text-slate-300 m-auto mt-6" />}
      <button onClick={onRemove} className="absolute top-0 right-0 bg-red-600 text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100" data-testid={`remove-photo-${photo.id}`}>
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function BriefingModal({ briefing, onClose, onAcknowledge }) {
  const su = briefing.service_user || {};
  const cp = briefing.care_plan_summary || {};
  const risks = briefing.headline_risks || [];
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-2" data-testid="briefing-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">First visit briefing</p>
          <h2 className="font-heading text-xl font-bold text-slate-900">
            {su.full_name}
            {su.preferred_name ? ` — "${su.preferred_name}"` : ""}
          </h2>
          <p className="text-xs text-slate-500">
            {briefing.is_first_visit ? "This is your first visit to this service user." : "The care plan has been updated since your last visit."} You must review this briefing before clocking in via Jibble.
          </p>
        </header>
        <div className="p-6 space-y-4 text-sm text-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Info label="Address" value={`${su.address?.line1 || ""}, ${su.address?.postcode || ""}`} />
            <Info label="Communication" value={su.communication_needs} />
            <Info label="Mental Capacity" value={(su.mca_status || "").replace(/_/g, " ")} />
            <Info label="DoLS" value={(su.dols_status || "").replace(/_/g, " ")} />
            {su.religion && <Info label="Religion" value={su.religion} />}
            {su.dietary_requirements && <Info label="Dietary" value={su.dietary_requirements} />}
          </div>
          {cp.about_me && <Block label="About me" body={cp.about_me} />}
          {cp.support_needs && <Block label="Support needs" body={cp.support_needs} />}
          {cp.daily_routine && <Block label="Daily routine" body={cp.daily_routine} />}
          {cp.communication && <Block label="Communication notes" body={cp.communication} />}
          {risks.length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-md p-3" data-testid="briefing-risks">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-1">Headline risks</p>
              {risks.map((r, i) => (
                <p key={i} className="text-sm text-red-900">
                  <strong>{r.risk_type}</strong> — residual {r.residual_band}. Controls: {(r.controls || []).join("; ") || "see risk assessment"}.
                </p>
              ))}
            </div>
          )}
          {cp.approved_at && (
            <p className="text-xs text-slate-500">
              Care plan v{cp.version_number}, approved {fmtDate(cp.approved_at)} by {cp.approved_by_name || "—"}.
            </p>
          )}
        </div>
        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-between gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md text-sm">
            Close
          </button>
          <button onClick={onAcknowledge} data-testid="briefing-ack-button" className="px-5 py-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-semibold text-sm">
            I have read and understood
          </button>
        </footer>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-800">{value || "—"}</p>
    </div>
  );
}

function Block({ label, body }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 whitespace-pre-wrap">{body}</p>
    </div>
  );
}
